import type {
  AbstractProductDefinition,
  Manufacturer,
  OptionDB,
  PartTableDefinition,
} from "@project/emerge";
import {
  MESH_INFO_MATERIAL_ALUMINUM_KEY,
  MESH_INFO_MATERIAL_NORMALS_KEY,
  MESH_INFO_MATERIAL_SHEETMETAL_KEY,
} from "@project/emerge-types";
import { LTProducts } from "./enum/LTProducts";
import type { LTPartType } from "./enum/PartType";
import { getRawAluminumMaterial } from "./materials/AluminumMaterial";
import { getNormalsMaterial } from "./materials/NormalsMaterial";
import { getRawSheetMetalMaterial } from "./materials/SheetMetalMaterial";
import { Case3d } from "./products/Case3d";
import { DoubleDoorCase } from "./products/DoubleDoorCase";
import { FlightCaseDefinition } from "./products/FlightCaseDefinition";
import { Haube } from "./products/Haube";
import { KeyboardCase } from "./products/KeyboardCase";
import { Koffer } from "./products/Koffer";
import { LCase } from "./products/LCase";
import { SingleDoorCase } from "./products/SingleDoorCase";
import { Truhe } from "./products/Truhe";
import type { PartRenderer } from "./renderers/renderers";
import {
  blueprintRenderers,
  measurementRenderer,
  partRenderers,
} from "./renderers/renderers";
import { tableRenderers } from "./renderers/tableRenderers";
import { CleanScene } from "./scenes/CleanScene";
import { GridFloorScene } from "./scenes/GridFloorScene";

export const manufacturer: Manufacturer<LTProducts, LTPartType> = {
  productDefinition: FlightCaseDefinition as unknown as new (
    settings: unknown,
    optionDb: OptionDB,
  ) => AbstractProductDefinition,
  products: {
    [LTProducts.Koffer]:
      Koffer as unknown as typeof AbstractProductDefinition & {
        getSerializedDefaultVersion: (
          optionDb: OptionDB,
          options?: unknown,
        ) => unknown;
        getAllowedVariableOptions: (
          optionDb: OptionDB,
          cuts: unknown[],
        ) => unknown;
      },
    [LTProducts.KeyboardCase]:
      KeyboardCase as unknown as typeof AbstractProductDefinition & {
        getSerializedDefaultVersion: (
          optionDb: OptionDB,
          options?: unknown,
        ) => unknown;
        getAllowedVariableOptions: (
          optionDb: OptionDB,
          cuts: unknown[],
        ) => unknown;
      },
    [LTProducts.Haube]: Haube as unknown as typeof AbstractProductDefinition & {
      getSerializedDefaultVersion: (
        optionDb: OptionDB,
        options?: unknown,
      ) => unknown;
      getAllowedVariableOptions: (
        optionDb: OptionDB,
        cuts: unknown[],
      ) => unknown;
    },
    [LTProducts.Truhe]: Truhe as unknown as typeof AbstractProductDefinition & {
      getSerializedDefaultVersion: (
        optionDb: OptionDB,
        options?: unknown,
      ) => unknown;
      getAllowedVariableOptions: (
        optionDb: OptionDB,
        cuts: unknown[],
      ) => unknown;
    },
    [LTProducts.Case3d]:
      Case3d as unknown as typeof AbstractProductDefinition & {
        getSerializedDefaultVersion: (
          optionDb: OptionDB,
          options?: unknown,
        ) => unknown;
        getAllowedVariableOptions: (
          optionDb: OptionDB,
          cuts: unknown[],
        ) => unknown;
      },
    [LTProducts.LCase]: LCase as unknown as typeof AbstractProductDefinition & {
      getSerializedDefaultVersion: (
        optionDb: OptionDB,
        options?: unknown,
      ) => unknown;
      getAllowedVariableOptions: (
        optionDb: OptionDB,
        cuts: unknown[],
      ) => unknown;
    },
    [LTProducts.SingleDoor]:
      SingleDoorCase as unknown as typeof AbstractProductDefinition & {
        getSerializedDefaultVersion: (
          optionDb: OptionDB,
          options?: unknown,
        ) => unknown;
        getAllowedVariableOptions: (
          optionDb: OptionDB,
          cuts: unknown[],
        ) => unknown;
      },
    [LTProducts.DoubleDoor]:
      DoubleDoorCase as unknown as typeof AbstractProductDefinition & {
        getSerializedDefaultVersion: (
          optionDb: OptionDB,
          options?: unknown,
        ) => unknown;
        getAllowedVariableOptions: (
          optionDb: OptionDB,
          cuts: unknown[],
        ) => unknown;
      },
  },
  blueprintRenderers,
  measurementRenderer,
  partRenderers: partRenderers as Record<LTPartType, PartRenderer>,
  partTables: tableRenderers as Record<LTPartType, PartTableDefinition>,
  materials: {
    [MESH_INFO_MATERIAL_ALUMINUM_KEY]: {
      name: "Aluminium",
      material: getRawAluminumMaterial(),
    },
    [MESH_INFO_MATERIAL_SHEETMETAL_KEY]: {
      name: "Blech",
      material: getRawSheetMetalMaterial(),
    },
    [MESH_INFO_MATERIAL_NORMALS_KEY]: {
      name: "Normalen",
      material: getNormalsMaterial(),
    },
  },
  scenes: [
    {
      id: "default",
      name: "Grid Floor",
      component: GridFloorScene,
    },
    {
      id: "clean",
      name: "Ohne Hintergrund",
      component: CleanScene,
    },
  ],
};
